if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', validateDependencies);
} else {
  validateDependencies();
}

function validateDependencies() {
  if (typeof FoxTheme === 'undefined') {
    console.warn('[Theme Script] FoxTheme library not loaded');
  }
  if (typeof Shopify === 'undefined') {
    console.warn('[Theme Script] Shopify object not available');
  }
}

class BasicHeader extends HTMLElement {
  constructor() {
    super();
    this._heightSetAttempts = 0;
    this._maxHeightRetries = 3;
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();

    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      const section = this.closest('.shopify-section');
      if (section) {
        section.addEventListener('shopify:section:load', this.init.bind(this));
        section.addEventListener('shopify:section:unload', this.init.bind(this));
        section.addEventListener('shopify:section:reorder', this.init.bind(this));
      }
    }
  }

  init() {
    if (typeof FoxTheme !== 'undefined' && FoxTheme.delayUntilInteraction) {
      new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));
    } else {
      this.setHeight();
    }

    const headerSection = this.headerSection;
    if (headerSection && this.enableTransparent) {
      headerSection.classList.add('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      const headerSection = this.headerSection;

      if (this.offsetHeight === 0) {
        if (this._heightSetAttempts < this._maxHeightRetries) {
          this._heightSetAttempts++;
          setTimeout(() => this.setHeight(), 100);
          return;
        }
      }

      this._heightSetAttempts = 0;

      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = this.parentElement ? Math.round(this.parentElement.offsetTop) : 0;

      if (offsetHeight > 0) {
        document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      }

      if (offsetTop >= 0) {
        document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
      }
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();
    this.classes = {
      pinned: 'header-pinned',
    };
    this.headerBounds = null;
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;

    const headerSection = this.headerSection;
    if (headerSection) {
      this.headerBounds = headerSection.getBoundingClientRect();
      this.beforeInit();
    }
  }

  beforeInit() {
    const headerSection = this.headerSection;
    if (!headerSection) {
      console.warn('[StickyHeader] Header section not found');
      return;
    }

    headerSection.classList.add('header-sticky');
    headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.onScrollHandler.bind(this), false);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    if (!this.headerBounds || !headerSection) return;

    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add('header-scrolled');

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.classes.pinned);
        } else {
          if (scrollTop < this.currentScrollTop || scrollTop < headerBoundsBottom + 100) {
            document.body.classList.add(this.classes.pinned);
          } else {
            document.body.classList.remove(this.classes.pinned);
          }
        }
      } else {
        headerSection.classList.remove('header-scrolled');
        document.body.classList.remove(this.classes.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

const clearDropdownCount = new WeakMap();

class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('[data-content]') || this.lastElementChild;

    if (!this.summaryElement || !this.contentElement) {
      console.warn('[DetailsDropdown] Missing required elements (summary or content)', this);
      return;
    }

    this._open = this.hasAttribute('open');

    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement || !this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener);
    this.addEventListener('mouseleave', this.detectHoverListener);
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    const isTouch = (typeof FoxTheme !== 'undefined' && FoxTheme.config && FoxTheme.config.isTouch) || false;

    if (isTouch) {
      return 'click';
    }

    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleSummaryClick(event) {
    event.preventDefault();

    const isTouch = (typeof FoxTheme !== 'undefined' && FoxTheme.config && FoxTheme.config.isTouch) || false;

    if (!isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (!this.contentElement) return;

    if (value) {
      const currentCount = (clearDropdownCount.get(DetailsDropdown) || 0) + 1;
      clearDropdownCount.set(DetailsDropdown, currentCount);

      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => {
        if (this.contentElement) {
          this.contentElement.setAttribute('open', '');
        }
      }, 100);

      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      await this.showWithTransition();

      this.needsReverse();

      if (typeof FoxTheme !== 'undefined' && FoxTheme.utils && FoxTheme.utils.waitForEvent) {
        return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
      }
    } else {
      const currentCount = Math.max((clearDropdownCount.get(DetailsDropdown) || 1) - 1, 0);
      clearDropdownCount.set(DetailsDropdown, currentCount);

      document.body.classList.toggle(this.classes.bodyClass, currentCount > 0);

      this.summaryElement.removeAttribute('open');
      if (this.contentElement) {
        this.contentElement.removeAttribute('open');
      }
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);

      await this.hideWithTransition();

      if (!this.open) {
        this.removeAttribute('open');
      }

      if (typeof FoxTheme !== 'undefined' && FoxTheme.utils && FoxTheme.utils.waitForEvent) {
        return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
      }
    }
  }

  async showWithTransition() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.Motion) {
      if (this.contentElement) {
        this.contentElement.style.opacity = '1';
        this.contentElement.style.visibility = 'visible';
      }
      return Promise.resolve();
    }

    const motionReduced = FoxTheme.config && FoxTheme.config.motionReduced;
    const childElement = this.contentElement.firstElementChild;

    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: motionReduced ? 0 : 0.1,
      }
    );

    const translateY = this.level === 'top' ? '-105%' : '2rem';

    if (!childElement) return Promise.resolve();

    return FoxTheme.Motion.animate(
      childElement,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.Motion) {
      if (this.contentElement) {
        this.contentElement.style.opacity = '0';
        this.contentElement.style.visibility = 'hidden';
      }
      return Promise.resolve();
    }

    const motionReduced = FoxTheme.config && FoxTheme.config.motionReduced;
    const childElement = this.contentElement.firstElementChild;

    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );

    const translateY = this.level === 'top' ? '-105%' : '2rem';

    if (!childElement) return Promise.resolve();

    return FoxTheme.Motion.animate(
      childElement,
      { transform: `translateY(${translateY})` },
      {
        duration: motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      this.open = event.type === 'mouseenter';
    }
  }

  needsReverse() {
    if (!this.contentElement) return;

    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.Motion) {
      if (this.contentElement && this.contentElement.firstElementChild) {
        this.contentElement.firstElementChild.style.visibility = 'visible';
        this.contentElement.firstElementChild.style.transform = 'translateY(0)';
      }
      return Promise.resolve();
    }

    const childElement = this.contentElement.firstElementChild;
    if (!childElement) return Promise.resolve();

    const motionReduced = FoxTheme.config && FoxTheme.config.motionReduced;

    return FoxTheme.Motion.animate(
      childElement,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.Motion) {
      if (this.contentElement && this.contentElement.firstElementChild) {
        this.contentElement.firstElementChild.style.visibility = 'hidden';
        this.contentElement.firstElementChild.style.transform = 'translateY(-100%)';
      }
      return Promise.resolve();
    }

    const childElement = this.contentElement.firstElementChild;
    if (!childElement) return Promise.resolve();

    const motionReduced = FoxTheme.config && FoxTheme.config.motionReduced;

    return FoxTheme.Motion.animate(
      childElement,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

customElements.define('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    const summary = this.querySelector('summary');
    const closeButton = this.querySelector('button');

    if (summary) {
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    }

    if (closeButton) {
      closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
    }
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick() {
    setTimeout(() => {
      const parent = this.parent;
      if (parent) {
        parent.classList.add('active');
      }
      this.classList.add('active');
      const summary = this.summary;
      if (summary) {
        summary.setAttribute('aria-expanded', 'true');
      }
    }, 100);
  }

  onCloseButtonClick() {
    const parent = this.parent;
    if (parent) {
      parent.classList.remove('active');
    }
    this.classList.remove('active');
    const summary = this.summary;
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
    }

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
  }

  get menuItems() {
    if (!this._menuItems) {
      this._menuItems = this.querySelectorAll('.menu-drawer__menus:not(.active) > li');
    }
    return this._menuItems;
  }

  connectedCallback() {
    if (typeof DrawerComponent !== 'undefined') {
      Object.setPrototypeOf(this, DrawerComponent.prototype);
      this.prepareToShow = MenuDrawer.prototype.prepareToShow;
      this.handleAfterHide = MenuDrawer.prototype.handleAfterHide;
      this.animateMenuItems = MenuDrawer.prototype.animateMenuItems;
    }
  }

  prepareToShow() {
    if (typeof FoxTheme !== 'undefined' && FoxTheme.Motion) {
      setTimeout(() => {
        this.animateMenuItems();
      }, 300);
    }
  }

  animateMenuItems() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.Motion) {
      return;
    }

    if (FoxTheme.config && FoxTheme.config.motionReduced) return;

    FoxTheme.Motion.animate(
      this.menuItems,
      {
        transform: ['translateX(-20px)', 'translateX(0)'],
        opacity: [0, 1],
      },
      {
        duration: 0.6,
        easing: [0.075, 0.82, 0.165, 1],
        delay: FoxTheme.Motion.stagger ? FoxTheme.Motion.stagger(0.1) : undefined,
      }
    ).finished.then(() => {
      this.menuItems.forEach((item) => item.removeAttribute('style'));
    });
  }

  handleAfterHide() {
    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        if (menu.onCloseButtonClick) {
          menu.onCloseButtonClick();
        }
      });
    });
  }
}

customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns || '1');
  }

  initSlide() {
    const container = this.container;

    if (!container) {
      console.warn('[MenuProductList] Swiper container not found');
      return;
    }

    if (typeof FoxTheme === 'undefined' || !FoxTheme.Carousel) {
      console.warn('[MenuProductList] FoxTheme.Carousel not available');
      return;
    }

    const slider = new FoxTheme.Carousel(container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label',
    });
    slider.init();

    if (typeof FoxTheme !== 'undefined' && FoxTheme.a11y && FoxTheme.a11y.getFocusableElements) {
      const focusableElements = FoxTheme.a11y.getFocusableElements(this);

      focusableElements.forEach((element) => {
        element.addEventListener('focusin', function () {
          const slide = element.closest('.swiper-slide');

          if (slide && slider.slider) {
            slider.slider.slideTo(slider.slider.slides.indexOf(slide));
          }
        });
      });
    }
  }
}

customElements.define('menu-product-list', MenuProductList);

class HeaderAccount extends HTMLElement {
  constructor() {
    super();
    this._touchEnabled = this.isTouchDevice();
  }

  isTouchDevice() {
    return (
      typeof window !== 'undefined' &&
      ('ontouchstart' in window ||
        (window.DocumentTouch && document instanceof window.DocumentTouch) ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0)
    );
  }

  async connectedCallback() {
    const account = await this.waitForElement('shopify-account', 5000);

    if (!account) {
      console.warn('[HeaderAccount] shopify-account element not found after 5 seconds');
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 100));

    this.injectAnimation(account);

    if (this._touchEnabled) {
      this.fixTriggerTouchClick(account);
    }

    if (this._touchEnabled) {
      this.fixModalInteractivity(account);
    }
  }

  waitForElement(selector, timeout = 5000) {
    return new Promise((resolve) => {
      const startTime = Date.now();

      const checkElement = () => {
        const element = this.querySelector(selector);

        if (element) {
          resolve(element);
          return;
        }

        if (Date.now() - startTime > timeout) {
          resolve(null);
          return;
        }

        requestAnimationFrame(checkElement);
      };

      checkElement();
    });
  }

  injectAnimation(account) {
    const shadow = account.shadowRoot;

    if (!shadow) {
      console.warn('[HeaderAccount] shadowRoot not accessible on shopify-account');
      return;
    }

    if (shadow.querySelector('#custom-account-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'custom-account-styles';

    style.textContent = `
      @keyframes dialogScaleIn {
        0% { transform: scale(0.8); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes dialogScaleOut {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(0.8); opacity: 0; }
      }
      @keyframes dialogBackdropIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes dialogBackdropOut {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }

      dialog,
      dialog *,
      dialog::backdrop {
        pointer-events: auto;
      }

      @media (min-width: 751px) {
        dialog .account {
          height: fit-content;
          background: var(--shopify-account-color-background);
          border-radius: inherit;
        }

        dialog[open] {
          animation: dialogScaleIn 0.6s cubic-bezier(0.7, 0, 0.2, 1);
        }

        dialog.closing {
          animation: dialogScaleOut 0.6s cubic-bezier(0.7, 0, 0.2, 1);
        }

        dialog[open]::backdrop {
          cursor: zoom-out;
          animation: dialogBackdropIn 0.8s cubic-bezier(0.7, 0, 0.2, 1);
        }

        dialog.closing::backdrop {
          animation: dialogBackdropOut 0.8s cubic-bezier(0.7, 0, 0.2, 1);
        }
      }

      @media (max-width: 750px) {
        dialog .account__content {
          padding-bottom: 2.4rem;
        }
      }
    `;

    shadow.prepend(style);
  }

  fixTriggerTouchClick(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    const triggerButton =
      shadow.querySelector('[slot="signed-out-avatar"]')?.closest('button') ||
      shadow.querySelector('button[aria-haspopup]') ||
      shadow.querySelector('button');

    if (!triggerButton) {
      console.warn('[HeaderAccount] Trigger button not found inside shopify-account shadow root');
      return;
    }

    if (triggerButton.dataset.touchFixApplied === 'true') return;
    triggerButton.dataset.touchFixApplied = 'true';

    let suppressNextClick = false;

    triggerButton.addEventListener(
      'touchend',
      (e) => {
        if (e.changedTouches && e.changedTouches.length > 1) return;

        suppressNextClick = true;
        e.preventDefault();

        triggerButton.click();
        setTimeout(() => {
          suppressNextClick = false;
        }, 400);
      },
      { passive: false }
    );

    triggerButton.addEventListener('click', (e) => {
      if (suppressNextClick) {
        suppressNextClick = false;
      }
    });
  }

  fixModalInteractivity(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    if (shadow.dataset && shadow.host && shadow.host.dataset.modalTouchFixApplied === 'true') return;
    if (account.dataset.modalTouchFixApplied === 'true') return;
    account.dataset.modalTouchFixApplied = 'true';

    const recentlyHandled = new WeakSet();

    shadow.addEventListener(
      'touchend',
      (e) => {
        const dialog = shadow.querySelector('dialog[open]');
        if (!dialog) return;

        if (e.changedTouches && e.changedTouches.length > 1) return;

        const path = e.composedPath();
        const target = path.find((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches('button, a, [role="button"], input, textarea, select, label');
        });

        if (!target) return;

        const tag = target.tagName;
        const type = (target.getAttribute('type') || '').toLowerCase();

        if (tag === 'INPUT' && (type === 'checkbox' || type === 'radio')) {
          e.preventDefault();
          recentlyHandled.add(target);
          target.click();
        } else if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
          recentlyHandled.add(target);
          target.focus();
        } else {
          e.preventDefault();
          recentlyHandled.add(target);
          target.click();
        }

        setTimeout(() => recentlyHandled.delete(target), 400);
      },
      { passive: false }
    );
  }
}

customElements.define('header-account', HeaderAccount);